import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { makeRequestAuth } from "../../../_config/api";

const BarChart = () => {
    const [fetchedData, setFetchedData] = useState({ categories: [], propertyCount: [] });
    const [year, setYear] = useState("2026");
    const [status, setStatus] = useState("All");
    const [propertyType, setPropertyType] = useState("");
    const [city, setCity] = useState("");
    const [cities, setCities] = useState(["All Cities"]);

    const years = ["2025", "2026", "2027", "2028", "2029", "2030"];
    const statuses = ["All", "approved", "pending", "rejected", "sold"];

    const propertyTypes = [
        { label: "All Property Types", value: "" },
        { label: "Apartment", value: "apartment" },
        { label: "Villa", value: "villa" },
        { label: "Land / Plot", value: "land" },
        { label: "Commercial", value: "other-commercial" },
        { label: "House / Home", value: "Home" },
        { label: "Office", value: "office" },
        { label: "Shop", value: "shop" },
        { label: "Building", value: "building" },
        { label: "Studio", value: "studio" },
        { label: "Penthouse", value: "penthouse" },
    ];

    // Dynamic Cities Fetch Effect
    useEffect(() => {
        const fetchCities = async () => {
            try {
                const res = await makeRequestAuth("post", "/admin/getCityList", { keyword: "" });
                if (res?.code === 0 && Array.isArray(res.data)) {
                    const cityNames = res.data.map(c => c.cityName).filter(Boolean);
                    setCities(["All Cities", ...cityNames]);
                }
            } catch (err) {
                console.error("City fetch failed", err);
            }
        };
        fetchCities();
    }, []);

    // Chart Data Fetch Effect
    useEffect(() => {
        const fetchChartData = async () => {
            try {
                const resData = { 
                    year: year, 
                    status: status === "All" ? "" : status,
                    propertyType: propertyType,
                    city: city === "All Cities" ? "" : city
                };
                const response = await makeRequestAuth("post", "/admin/propertiesChart", resData);

                if (response?.code === 0 && !response.error && response?.data) {
                    setFetchedData(response.data);
                } else {
                    console.error("Server Error:", response?.data?.message || "Unknown error");
                }
            } catch (error) {
                console.error("Request Failed:", error);
            }
        };

        fetchChartData();
    }, [year, status, propertyType, city]);

    const defaultCategories = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const chartCategories = Array.isArray(fetchedData?.categories) && fetchedData.categories.length > 0
        ? fetchedData.categories
        : defaultCategories;

    const propertyCountData = chartCategories.map((_, idx) => 
        Number(fetchedData?.propertyCount?.[idx]) || 0
    );

    const calculatedMax = Math.max(...propertyCountData, 0);
    const maxVal = calculatedMax > 0 ? calculatedMax : 48;

    // Selected labels for custom tooltip
    const selectedTypeLabel = propertyTypes.find(p => p.value === propertyType)?.label || "All Property Types";
    const selectedCityLabel = city === "" || city === "All Cities" ? "All Cities" : city;
    const selectedStatusLabel = status === "All" ? "All Statuses" : status.charAt(0).toUpperCase() + status.slice(1);

    const chartOptions = {
        chart: {
            type: "bar",
            toolbar: { show: false },
        },
        xaxis: {
            categories: chartCategories,
            labels: {
                style: {
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#6b7280",
                },
            },
        },
        yaxis: {
            tickAmount: 4,
            min: 0,
            max: maxVal,
            labels: {
                formatter: (val) => {
                    if (val === undefined || val === null || isNaN(val)) return "0";
                    return Number.isInteger(val) ? `${val}` : `${val.toFixed(2)}`;
                },
                style: {
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#6b7280",
                },
            },
        },
        grid: {
            yaxis: { lines: { show: false } },
        },
        colors: ["#ffbf1a"],
        fill: {
            type: "gradient",
            gradient: {
                shade: "light",
                type: "vertical",
                shadeIntensity: 0.5,
                gradientToColors: ["#ff4080"],
                opacityFrom: 1,
                opacityTo: 1,
                stops: [0, 100],
            },
        },
        plotOptions: {
            bar: {
                columnWidth: "35%",
                borderRadius: 5,
            },
        },
        dataLabels: {
            enabled: false,
        },
        tooltip: {
            theme: "light",
            custom: function({ series, seriesIndex, dataPointIndex, w }) {
                const monthName = chartCategories[dataPointIndex];
                const count = series[seriesIndex][dataPointIndex] || 0;
                return `
                    <div style="padding: 10px; font-family: inherit; font-size: 12px; line-height: 1.5;">
                        <div style="font-weight: bold; margin-bottom: 4px; color: #374151;">${monthName} ${year}</div>
                        <div style="color: #4b5563;"><strong>Properties Added:</strong> ${count}</div>
                        <div style="color: #4b5563;"><strong>Property Type:</strong> ${selectedTypeLabel}</div>
                        <div style="color: #4b5563;"><strong>Status:</strong> ${selectedStatusLabel}</div>
                        <div style="color: #4b5563;"><strong>City:</strong> ${selectedCityLabel}</div>
                    </div>
                `;
            }
        },
    };

    const chartSeries = [
        {
            name: "Properties",
            data: propertyCountData,
        },
    ];

    return (
        <div className="bg-white p-4 rounded-2xl shadow-md w-full mx-auto">
            <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
                <h2 className="text-sm font-semibold text-gray-700">
                    Total Properties By Month
                </h2>

                <div className="flex gap-2 flex-wrap">
                    {/* 1. Year Filter */}
                    <select
                        className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700"
                        value={year}
                        onChange={(e) => setYear(e.target.value)}
                    >
                        {years.map((y) => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>

                    {/* 2. Property Type Filter */}
                    <select
                        className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700"
                        value={propertyType}
                        onChange={(e) => setPropertyType(e.target.value)}
                    >
                        {propertyTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                                {type.label}
                            </option>
                        ))}
                    </select>

                    {/* 3. Status Filter */}
                    <select
                        className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        {statuses.map((s) => (
                            <option key={s} value={s}>
                                {s === "All" ? "All Statuses" : s.charAt(0).toUpperCase() + s.slice(1)}
                            </option>
                        ))}
                    </select>

                    {/* 4. City Filter */}
                    <select
                        className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                    >
                        {cities.map((c) => (
                            <option key={c} value={c}>
                                {c}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <Chart
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={235}
            />
        </div>
    );
};

export default BarChart;
